interface Extremes {
  min: number;
  max: number;
}

export interface ResponseItem {
  x: number;
  y: number;
  time: string;
  itens?: number;
}

export const means = (
  data: number[][],
  divider: number,
): Promise<{ x: number; y: number; time: string; itens: number }[]> => {
  return new Promise((resolve) => {
    let means: number[][] = [];
    const assignments: number[] = [];
    let dataExtremes: Extremes[];
    let dataRange: number[];
    let sizes: number[];

    function getDataRanges(extremes: Extremes[]) {
      const ranges: number[] = [];
      for (let dimension = 0; dimension < extremes.length; dimension += 1) {
        ranges[dimension] = extremes[dimension].max - extremes[dimension].min;
      }
      return ranges;
    }

    function getDataExtremes(points: number[][]) {
      const extremes: Extremes[] = [];
      let pos = 0;
      while (pos < points.length) {
        const point = points[pos];
        for (let dimension = 0; dimension < point.length; dimension += 1) {
          if (!extremes[dimension]) {
            extremes[dimension] = { min: 1000, max: 0 };
          }
          if (point[dimension] < extremes[dimension].min) {
            extremes[dimension].min = point[dimension];
          }
          if (point[dimension] > extremes[dimension].max) {
            extremes[dimension].max = point[dimension];
          }
        }
        pos += 1;
      }
      return extremes;
    }

    function initMeans(k: number) {
      let counter = k;
      while (counter) {
        const mean: number[] = [];
        for (let dimension = 0; dimension < dataExtremes.length; dimension += 1) {
          mean[dimension] = dataExtremes[dimension].min + Math.random() * dataRange[dimension];
        }
        means.push(mean);
        counter -= 1;
      }
      return means;
    }

    function makeAssignments() {
      let i = 0;
      while (i < data.length) {
        const point = data[i];
        const distances: number[] = [];

        for (let j = 0; j < means.length; j += 1) {
          const mean = means[j];
          let sum = 0;
          for (let dimension = 0; dimension < point.length; dimension += 1) {
            let difference = point[dimension] - mean[dimension];
            difference *= difference;
            sum += difference;
          }
          distances[j] = Math.sqrt(sum);
        }

        assignments[i] = distances.indexOf(Math.min.apply(null, distances));
        i += 1;
      }
    }

    function moveMeans(): boolean {
      makeAssignments();

      const sums: number[][] = Array(means.length);
      const counts: number[] = Array(means.length);
      let moved = false;
      for (let j = 0; j < means.length; j += 1) {
        counts[j] = 0;
        sums[j] = Array(means[j].length);
        for (let dimension = 0; dimension < means[j].length; dimension += 1) {
          sums[j][dimension] = 0;
        }
      }

      let pointIndex = 0;
      while (pointIndex < assignments.length) {
        const meanIndex = assignments[pointIndex];
        const point = data[pointIndex];
        const mean = means[meanIndex];

        counts[meanIndex] += 1;

        for (let dimension = 0; dimension < mean?.length; dimension += 1) {
          sums[meanIndex][dimension] += point[dimension];
        }
        pointIndex += 1;
      }

      for (let indx = 0; indx < sums.length; indx += 1) {
        if (counts[indx] === 0) {
          sums[indx] = means[indx];
          for (let dimension = 0; dimension < dataExtremes.length; dimension += 1) {
            sums[indx][dimension] =
              dataExtremes[dimension].min + Math.random() * dataRange[dimension];
          }
        } else {
          for (let dimension = 0; dimension < sums[indx].length; dimension += 1) {
            sums[indx][dimension] /= counts[indx];
          }
        }
      }

      if (means.toString() !== sums.toString()) {
        moved = true;
        sizes = counts;
      }

      means = sums;

      return moved;
    }

    function run() {
      const moved = moveMeans();

      if (moved) {
        run();
      } else {
        const result = [];
        for (let i = 0; i < means.length; i += 1) {
          const point = means[i];
          const x = point[0];
          const y = point[1];
          result.push({ x, y, itens: sizes?.[i] });
        }
        resolve(result as { x: number; y: number; time: string; itens: number }[]);
      }
    }

    function setup() {
      dataExtremes = getDataExtremes(data);
      dataRange = getDataRanges(dataExtremes);
      means = initMeans(divider);
      makeAssignments();
      run();
    }

    setup();
  });
};
